/**
 * SmartInput-Familie für DACH-spezifische Eingaben.
 *
 * Alle Komponenten teilen sich das Verhalten:
 *   - locale-aware Auto-Format on blur
 *   - Live-Validierung (Error unter dem Feld)
 *   - country-Prop steuert das Verhalten (default: 'DE')
 *   - kompatibel mit react-hook-form (forwardRef + onChange/onBlur)
 *   - data-testid wird durchgereicht
 *
 * Komponenten:
 *   - <CurrencyInput />
 *   - <IBANInput />
 *   - <VATInput />
 *   - <PhoneInput />
 *   - <PostcodeInput />
 *   - <DateInputDACH />
 */
import React, { forwardRef, useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
  validateIBAN,
  formatIBAN,
  normalizeIBAN,
  getIBANHint,
  validateVatId,
  formatVatId,
  getVatIdHint,
  getVatIdPlaceholder,
  validatePostcode,
  getPostcodeHint,
  validatePhone,
  normalizePhone,
  formatPhone,
  getPhonePrefix,
  parseCurrency,
  formatCurrency,
  getCurrencySymbol,
  parseDateDACH,
  formatDateDACH,
} from '@/utils/i18nValidators';

// ────────── Shared Wrapper ──────────

function FieldWrapper({ label, hint, error, required, children, testid }) {
  return (
    <div className="w-full">
      {label && (
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint && !error && (
        <p className="mt-1 text-xs text-slate-400" data-testid={testid ? `${testid}-hint` : undefined}>
          {hint}
        </p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid={testid ? `${testid}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}

// ────────── Currency ──────────

export const CurrencyInput = forwardRef(function CurrencyInput(
  { value, onChange, onBlur: onBlurProp, country = 'DE', label, hint, required, placeholder, disabled, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const symbol = getCurrencySymbol(country);
  const [display, setDisplay] = useState(() =>
    typeof value === 'number' ? formatCurrency(value, country, { style: 'decimal' }) : (value || '')
  );

  useEffect(() => {
    if (typeof value === 'number') {
      setDisplay(formatCurrency(value, country, { style: 'decimal' }));
    } else if (value === null || value === undefined || value === '') {
      setDisplay('');
    }
  }, [value, country]);

  const handleChange = (e) => {
    const v = e.target.value;
    setDisplay(v);
    const num = parseCurrency(v, country);
    onChange?.(num, v);
  };

  const handleBlur = (e) => {
    const num = parseCurrency(display, country);
    if (num !== null) {
      setDisplay(formatCurrency(num, country, { style: 'decimal' }));
    }
    onBlurProp?.(e);
  };

  return (
    <FieldWrapper label={label} hint={hint} required={required} testid={testid}>
      <div className="relative">
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder ?? (country === 'CH' ? "1'234.56" : '1.234,56')}
          disabled={disabled}
          className={`pr-12 ${className}`}
          data-testid={testid}
          {...rest}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
          {symbol}
        </span>
      </div>
    </FieldWrapper>
  );
});

// ────────── IBAN ──────────

export const IBANInput = forwardRef(function IBANInput(
  { value, onChange, onBlur: onBlurProp, onBankFound, country = 'DE', label = 'IBAN', required, placeholder, disabled, validateOnBlur = true, lookupBank = false, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const [display, setDisplay] = useState(() => formatIBAN(value || ''));
  const [error, setError] = useState(null);
  const [bankName, setBankName] = useState(null);

  useEffect(() => {
    setDisplay(formatIBAN(value || ''));
  }, [value]);

  const handleChange = (e) => {
    const formatted = formatIBAN(e.target.value);
    setDisplay(formatted);
    setError(null);
    setBankName(null);
    onChange?.(normalizeIBAN(formatted), formatted);
  };

  const handleBlur = async (e) => {
    if (validateOnBlur && display) {
      // Feb 2026 — IBAN-Validierung Country-unabhängig (User in AT darf DE-IBAN haben).
      // Nur ISO-13616 mod-97-Checksum prüfen, keine Country-Längen-Bindung.
      const res = validateIBAN(display);
      setError(res.valid ? null : res.error);

      // Feb 2026 — Smart-Suggestion: IBAN → Bank-Name
      if (res.valid && (lookupBank || onBankFound)) {
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const r = await fetch(`${API}/api/i18n/iban-bank-lookup?iban=${encodeURIComponent(normalizeIBAN(display))}`);
          const data = await r.json();
          if (data.bank_name) {
            setBankName(data.bank_name);
            onBankFound?.(data.bank_name);
          }
        } catch { /* silent */ }
      }
    }
    onBlurProp?.(e);
  };

  const exampleByCountry = { DE: 'DE89 3704 0044 0532 0130 00', AT: 'AT48 3200 0000 1234 5864', CH: 'CH56 0483 5012 3456 7800 9' };

  // Smart-Hint zeigt entweder Standard ODER gefundene Bank
  const dynamicHint = bankName ? `→ ${bankName}` : getIBANHint(country);

  return (
    <FieldWrapper
      label={label}
      hint={dynamicHint}
      error={error}
      required={required}
      testid={testid}
    >
      <Input
        ref={ref}
        type="text"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder ?? exampleByCountry[country] ?? exampleByCountry.DE}
        disabled={disabled}
        className={`font-mono ${className}`}
        data-testid={testid}
        {...rest}
      />
    </FieldWrapper>
  );
});

// ────────── USt-IdNr / VAT-ID ──────────

export const VATInput = forwardRef(function VATInput(
  { value, onChange, onBlur: onBlurProp, country = 'DE', label = 'USt-IdNr', required, disabled, validateOnBlur = true, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const [display, setDisplay] = useState(value || '');
  const [error, setError] = useState(null);

  useEffect(() => {
    setDisplay(value || '');
  }, [value]);

  const handleChange = (e) => {
    setDisplay(e.target.value);
    setError(null);
    onChange?.(e.target.value);
  };

  const handleBlur = (e) => {
    if (display) {
      const formatted = formatVatId(display, country);
      setDisplay(formatted);
      onChange?.(formatted);
      if (validateOnBlur) {
        const res = validateVatId(formatted, country);
        setError(res.valid ? null : res.error);
      }
    } else {
      setError(null);
    }
    onBlurProp?.(e);
  };

  return (
    <FieldWrapper
      label={label}
      hint={getVatIdHint(country)}
      error={error}
      required={required}
      testid={testid}
    >
      <Input
        ref={ref}
        type="text"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={getVatIdPlaceholder(country)}
        disabled={disabled}
        className={`font-mono ${className}`}
        data-testid={testid}
        {...rest}
      />
    </FieldWrapper>
  );
});

// ────────── Phone ──────────

export const PhoneInput = forwardRef(function PhoneInput(
  { value, onChange, onBlur: onBlurProp, country = 'DE', label = 'Telefon', required, disabled, validateOnBlur = true, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const [display, setDisplay] = useState(value || '');
  const [error, setError] = useState(null);

  useEffect(() => {
    setDisplay(value || '');
  }, [value]);

  const handleChange = (e) => {
    setDisplay(e.target.value);
    setError(null);
    onChange?.(e.target.value);
  };

  const handleBlur = (e) => {
    if (display) {
      const norm = normalizePhone(display, country);
      setDisplay(formatPhone(norm));
      onChange?.(norm);
      if (validateOnBlur) {
        const res = validatePhone(display, country);
        setError(res.valid ? null : res.error);
      }
    } else {
      setError(null);
    }
    onBlurProp?.(e);
  };

  const placeholders = { DE: '+49 30 12345678', AT: '+43 1 1234567', CH: '+41 44 123 45 67' };

  return (
    <FieldWrapper
      label={label}
      hint={`Vorwahl: ${getPhonePrefix(country)}`}
      error={error}
      required={required}
      testid={testid}
    >
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholders[country] ?? placeholders.DE}
        disabled={disabled}
        className={className}
        data-testid={testid}
        {...rest}
      />
    </FieldWrapper>
  );
});

// ────────── Postcode ──────────

export const PostcodeInput = forwardRef(function PostcodeInput(
  { value, onChange, onBlur: onBlurProp, onCityFound, country = 'DE', label = 'Postleitzahl', required, disabled, validateOnBlur = true, lookupCity = false, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const [display, setDisplay] = useState(value || '');
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null); // PLZ → City lookup result

  useEffect(() => {
    setDisplay(value || '');
  }, [value]);

  const handleChange = (e) => {
    // Nur Ziffern erlauben
    const v = e.target.value.replace(/\D/g, '');
    setDisplay(v);
    setError(null);
    setSuggestion(null);
    onChange?.(v);
  };

  const handleBlur = async (e) => {
    if (display && validateOnBlur) {
      const res = validatePostcode(display, country);
      setError(res.valid ? null : res.error);

      // Feb 2026 — Smart-Suggestion: PLZ → Stadt-Lookup
      if (res.valid && (lookupCity || onCityFound)) {
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const r = await fetch(`${API}/api/i18n/postcode-lookup?plz=${encodeURIComponent(display)}&country=${country}`);
          const data = await r.json();
          if (data.found && data.city) {
            setSuggestion(data.city);
            onCityFound?.(data.city);
          }
        } catch { /* silent: lookup ist optional */ }
      }
    } else if (!display) {
      setError(null);
      setSuggestion(null);
    }
    onBlurProp?.(e);
  };

  const maxLength = country === 'DE' ? 5 : 4;
  const placeholders = { DE: '10115', AT: '1010', CH: '8001' };

  // Smart-Hint zeigt entweder Standard-Format ODER gefundene Stadt
  const dynamicHint = suggestion ? `→ ${suggestion}` : getPostcodeHint(country);

  return (
    <FieldWrapper
      label={label}
      hint={dynamicHint}
      error={error}
      required={required}
      testid={testid}
    >
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={maxLength}
        placeholder={placeholders[country] ?? placeholders.DE}
        disabled={disabled}
        className={className}
        data-testid={testid}
        {...rest}
      />
    </FieldWrapper>
  );
});

// ────────── Date (DACH-Format DD.MM.YYYY) ──────────

/**
 * DateInputDACH — verwendet HTML <input type="date"> intern, akzeptiert/liefert ISO-Strings,
 * zeigt aber DACH-Format an (DD.MM.YYYY) via toLocaleDateString.
 *
 * Browser-native rendert input[type=date] in der User-Locale, das ist meist OK.
 * Diese Komponente ist primär ein Wrapper für konsistentes Styling + Hilfsfunktionen.
 */
export const DateInputDACH = forwardRef(function DateInputDACH(
  { value, onChange, label, required, disabled, 'data-testid': testid, className = '', ...rest },
  ref
) {
  const handleChange = (e) => {
    onChange?.(e.target.value); // ISO-String YYYY-MM-DD
  };

  return (
    <FieldWrapper label={label} required={required} testid={testid}>
      <Input
        ref={ref}
        type="date"
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        className={className}
        data-testid={testid}
        {...rest}
      />
    </FieldWrapper>
  );
});

// Helper-Re-exports für Convenience
export { parseDateDACH, formatDateDACH };
